import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { relTime, initials } from "@/lib/orgUtils";
import { Plus, Search, Users, Lock, Globe, FileUp, Copy, Sparkles, X, Network } from "lucide-react";

const LAYOUT_LABEL = { classic: "Classic Tree", horizontal: "Horizontal", outline: "Outline", radial: "Radial", team_grid: "Team Grid" };

function NewChartWizard({ views, onClose }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [mode, setMode] = useState("empty");
  const [copySource, setCopySource] = useState("");
  const [copyRoot, setCopyRoot] = useState("");
  const [srcEmployees, setSrcEmployees] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (copySource) api.get(`/employees?chart_id=${copySource}`).then((r) => setSrcEmployees(r.data.filter((e) => e.status === "active")));
    else setSrcEmployees([]);
    setCopyRoot("");
  }, [copySource]);

  const create = async () => {
    if (!name.trim()) { toast.error("Give your chart a name"); return; }
    setBusy(true);
    try {
      const { data: view } = await api.post("/views", { name: name.trim() });
      if (mode === "copy" && copySource) {
        const { data } = await api.post(`/views/${view.id}/copy-people`, { source_view_id: copySource, root_employee_id: copyRoot || null });
        toast.success(`Chart created — ${data.copied} people copied in`);
        navigate(`/chart/${view.id}`);
      } else if (mode === "import") {
        toast.success("Chart created — now bring in your people");
        navigate(`/imports?chart=${view.id}`);
      } else {
        toast.success("Chart created — start adding people");
        navigate(`/chart/${view.id}`);
      }
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  const MODES = [
    { key: "empty", icon: Sparkles, title: "Start empty", desc: "Add people one by one on the canvas" },
    { key: "import", icon: FileUp, title: "Import a file", desc: "CSV, Excel or Google Sheet into this chart" },
    { key: "copy", icon: Copy, title: "Copy a branch", desc: "Duplicate people from an existing chart" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-[#181B34]/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-7" onClick={(e) => e.stopPropagation()} data-testid="new-chart-wizard">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-xl font-black tracking-tight">Create a new org chart</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F0F3FF] flex items-center justify-center"><X size={17} /></button>
        </div>
        <label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#676a82]">Chart name</label>
        <input data-testid="new-chart-name" autoFocus value={name} onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Engineering Department"
          className="mt-1.5 w-full h-12 px-4 rounded-xl border-2 border-[#181B34]/10 font-bold focus:outline-none focus:border-[#6161FF]" />
        <label className="mt-5 block text-[10px] font-bold uppercase tracking-[0.15em] text-[#676a82]">How do you want to start?</label>
        <div className="mt-2 space-y-2">
          {MODES.map((m) => (
            <button key={m.key} data-testid={`start-mode-${m.key}`} onClick={() => setMode(m.key)}
              className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 text-left ${mode === m.key ? "border-[#6161FF] bg-[#6161FF]/5" : "border-[#181B34]/10 hover:border-[#181B34]/25"}`}>
              <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${mode === m.key ? "bg-[#6161FF] text-white" : "bg-[#F0F3FF] text-[#6161FF]"}`}><m.icon size={18} /></span>
              <span>
                <p className="font-black text-sm">{m.title}</p>
                <p className="text-xs text-[#676a82] font-medium">{m.desc}</p>
              </span>
            </button>
          ))}
        </div>
        {mode === "copy" && (
          <div className="mt-4 space-y-2 p-3 rounded-xl bg-[#F6F7FB]">
            <select data-testid="copy-source-select" value={copySource} onChange={(e) => setCopySource(e.target.value)}
              className="w-full h-10 px-2 rounded-lg border border-[#181B34]/15 text-sm font-bold bg-white">
              <option value="">Choose source chart…</option>
              {views.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.headcount} people)</option>)}
            </select>
            {copySource && (
              <select data-testid="copy-root-select" value={copyRoot} onChange={(e) => setCopyRoot(e.target.value)}
                className="w-full h-10 px-2 rounded-lg border border-[#181B34]/15 text-sm font-bold bg-white">
                <option value="">Entire chart</option>
                {srcEmployees.map((e) => <option key={e.id} value={e.id}>Branch under: {e.full_name} — {e.designation}</option>)}
              </select>
            )}
          </div>
        )}
        <button data-testid="create-chart-btn" disabled={busy || !name.trim() || (mode === "copy" && !copySource)} onClick={create}
          className="mt-6 w-full h-12 rounded-xl bg-[#6161FF] hover:bg-[#5050E5] text-white font-bold disabled:opacity-40">
          {busy ? "Creating…" : "Create chart"}
        </button>
      </div>
    </div>
  );
}

export default function ChartsGalleryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [views, setViews] = useState(null);
  const [q, setQ] = useState("");
  const [wizard, setWizard] = useState(false);
  const isViewer = user?.role === "viewer";

  const load = useCallback(() => api.get("/views").then((r) => setViews(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  if (!views) return <div className="h-full flex items-center justify-center"><div className="w-8 h-8 rounded-full border-4 border-[#6161FF] border-t-transparent animate-spin" /></div>;

  const filtered = views.filter((v) => !q || v.name.toLowerCase().includes(q.toLowerCase()) || (v.created_by_name || "").toLowerCase().includes(q.toLowerCase()));
  const mine = filtered.filter((v) => v.created_by === user.id || (v.co_authors || []).includes(user.id));
  const others = filtered.filter((v) => !mine.includes(v));

  const Card = ({ v }) => (
    <button data-testid={`chart-card-${v.id}`} onClick={() => navigate(`/chart/${v.id}`)}
      className="group text-left p-5 rounded-2xl bg-white border border-[#181B34]/10 shadow-sm hover:shadow-[0_10px_30px_rgba(24,27,52,0.12)] hover:-translate-y-0.5 transition-transform">
      <div className="flex items-start justify-between gap-2">
        <span className="w-10 h-10 rounded-xl bg-[#6161FF]/10 text-[#6161FF] flex items-center justify-center group-hover:bg-[#6161FF] group-hover:text-white"><Network size={18} /></span>
        <div className="flex items-center gap-1.5">
          {v.is_published ? (
            <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#00C875]/15 text-[#00925a]"><Globe size={9} /> Published</span>
          ) : (
            <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FDAB3D]/15 text-[#c97a10]">Draft</span>
          )}
          {!v.can_edit && <span title="Read-only for you"><Lock size={12} className="text-[#9699AD]" /></span>}
        </div>
      </div>
      <h3 className="mt-3 font-black text-lg leading-tight truncate">{v.name}</h3>
      <p className="mt-0.5 text-xs font-semibold text-[#676a82] flex items-center gap-1.5">
        <Users size={12} /> {v.headcount} people · {LAYOUT_LABEL[v.default_layout] || v.default_layout}
      </p>
      <div className="mt-4 flex items-center gap-2">
        <span className="w-6 h-6 rounded-full bg-[#F0F3FF] text-[#6161FF] flex items-center justify-center text-[9px] font-bold">{initials(v.created_by_name || "Admin")}</span>
        <span className="text-xs font-bold text-[#4b4e63] truncate">{v.created_by_name || "Admin"}</span>
        {(v.co_authors || []).length > 0 && <span className="text-[10px] font-bold text-[#9699AD]">+{v.co_authors.length} co-author{v.co_authors.length > 1 ? "s" : ""}</span>}
        <span className="ml-auto text-[10px] font-semibold text-[#9699AD]">{relTime(v.created_at)}</span>
      </div>
    </button>
  );

  return (
    <div className="h-full overflow-auto p-8" data-testid="charts-gallery-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-black tracking-tight">Org Charts</h1>
            <p className="text-[#676a82] font-medium mt-1">{views.length} chart{views.length === 1 ? "" : "s"} in your workspace — every chart owns its own people.</p>
          </div>
          {!isViewer && (
            <button data-testid="gallery-new-chart-btn" onClick={() => setWizard(true)}
              className="flex items-center gap-2 px-5 h-11 rounded-xl bg-[#6161FF] hover:bg-[#5050E5] text-white font-bold">
              <Plus size={16} /> New chart
            </button>
          )}
        </div>
        <div className="mt-6 relative max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9699AD]" />
          <input data-testid="gallery-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search charts or owners…"
            className="w-full h-10 pl-9 pr-3 rounded-xl bg-white border border-[#181B34]/10 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#6161FF]/25" />
        </div>
        {mine.length > 0 && (
          <>
            <p className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-[#9699AD]">Your charts</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{mine.map((v) => <Card key={v.id} v={v} />)}</div>
          </>
        )}
        {others.length > 0 && (
          <>
            <p className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-[#9699AD]">{mine.length ? "All other charts" : "All charts"}</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{others.map((v) => <Card key={v.id} v={v} />)}</div>
          </>
        )}
        {filtered.length === 0 && (
          <div className="mt-16 text-center">
            <Network size={40} className="mx-auto text-[#C4C7D4]" />
            <p className="mt-3 font-bold text-[#676a82]">No charts yet — create the first one.</p>
          </div>
        )}
      </div>
      {wizard && <NewChartWizard views={views} onClose={() => setWizard(false)} />}
    </div>
  );
}
